import { GRADIENT_PRESETS, gradientPreset } from "@keepoinc/site-page-react-component";
import { Fragment, CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ImageFieldAction, ImageFieldRow } from "../../../../../../components/ImageField";
import ToggleSwitch from "../../../../../../components/ToggleSwitch";
import { Background } from "../../../../../../model/theme";
import { classNames } from "../../../../../../utils/styles";
import { Divider, fieldLabel } from "../../styles";
import { BackgroundMediaModal } from "../BackgroundMediaModal";
import { ColorField, FieldPair } from "../ColorField";
import { PatternPicker } from "../PatternPicker";

/** An arrow pointing where the gradient goes (180: down, 135: diagonal, 90: to the side). */
const Arrow = ({ angle }: { angle: number }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ transform: `rotate(${angle - 180}deg)` }}>
    <path d="M12 4v16" />
    <path d="M6 14l6 6 6-6" />
  </svg>
);

/** "Rola com a página": up and down arrows. */
const ScrollIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M12 3v18M7 7l5-4 5 4M7 17l5 4 5-4" />
  </svg>
);

/** "Fixo": a pin. */
const PinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M9 3h6M10 3v6l-3 4h10l-3-4V3M12 13v8" />
  </svg>
);

/** The gradient's directions: the angle the page component draws it at, or from the middle (radial). */
const DIRECTIONS = [
  { key: "up", angle: 0 },
  { key: "down", angle: 180 },
  { key: "right", angle: 90 },
  { key: "diagonal", angle: 135 },
  { key: "radial", angle: undefined },
] as const;

/** "Radial": circles from the middle. */
const RadialIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="10" opacity=".5" />
  </svg>
);

const MAX_DIM = 60;

/** How much of a slider is chosen, for its blue part. */
const fill = (value: number, min: number, max: number) =>
  ({ "--fill": `${((value - min) / (max - min)) * 100}%` }) as CSSProperties;

/** A pattern goes up with the content (as the page's) or stays still; how strong it is, 5-60%, 15% as the page draws it. */
const PATTERN_POSITIONS = ["scroll", "fixed"] as const;
const PATTERN_SIZES = ["small", "medium", "large"] as const;
const DEFAULT_PATTERN_OPACITY = 15;

/** How much the photo is blurred: 10-100%, 50% as always. */
const DEFAULT_BLUR = 50;
const MIN_BLUR = 10;
const MIN_PATTERN_OPACITY = 5;

/** The least an image or video shows (so it never disappears). */
const MIN_MEDIA_OPACITY = 10;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: "Inter", sans-serif;
  font-size: 0.8125rem;
  color: var(--color-neutral-white);

  .options {
    display: flex;
    gap: 0.5rem;

    /* every choice as tall as the fields beside it (52px, like the color fields) */
    button {
      flex: 1;
      height: 52px;
      padding: 0.5rem;
      font-weight: 600;
      color: inherit;
      background: var(--color-neutral-base);
      border: 2px solid transparent;
      border-radius: 0.75rem;

      &.selected {
        border-color: var(--color-brand-blue);
      }
    }
  }

  .label {
    ${fieldLabel}
  }

  .block {
    display: flex;
    flex-direction: column;
    gap: 0.375rem; /* the label as close as the color fields' */
  }

  /* a slider in the brand's colors: the part chosen in blue (--fill, set by fill()) */
  input[type="range"] {
    width: 100%;
    height: 4px;
    appearance: none;
    cursor: pointer;
    background: linear-gradient(
      to right,
      var(--color-brand-blue) var(--fill, 50%),
      rgba(255, 255, 255, 0.2) var(--fill, 50%)
    );
    border-radius: 2px;

    &::-webkit-slider-thumb {
      width: 16px;
      height: 16px;
      appearance: none;
      background: var(--color-neutral-white);
      border-radius: 50%;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      background: var(--color-neutral-white);
      border: 0;
      border-radius: 50%;
    }
  }

  /* the opacity in a box as tall and light as the field beside it, its value on the right */
  .opacity {
    gap: 0.375rem;

    .box {
      display: flex;
      gap: 1rem;
      align-items: center;
      height: 52px;
      padding: 0 1rem;
      background: var(--color-neutral-base);
      border-radius: 12px;
    }

    .value {
      min-width: 2.5rem;
      font-weight: 700;
      text-align: right;
    }
  }

  /* an icon beside the name */
  .options button {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
  }

  /* the ready-made gradients: a grid of thumbnails in the Keepo's colors */
  .presets {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.5rem;

    button {
      aspect-ratio: 4 / 3;
      border: 2px solid transparent;
      border-radius: 10px;

      &.selected {
        border-color: var(--color-brand-blue);
      }
    }
  }

  /* "Animar": its name and what it does, the switch on the right */
  .toggle {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;

    > span {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    label {
      height: 24px;
      margin-bottom: 0;
    }
  }

  /* as the alignment's options: an icon each, lighter than the card */
  .directions {
    display: flex;
    gap: 1rem;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 5.5rem;
      height: 48px;
      color: var(--color-neutral-white);
      background: var(--color-neutral-base);
      border: 2px solid transparent;
      border-radius: 12px;

      &.selected {
        border-color: var(--color-brand-blue);
      }
    }
  }

  .hint {
    opacity: 0.7;
  }

  .error {
    color: var(--color-feedback-error, #ff6b6b);
  }

  .video input {
    display: none;
  }

  input[type="range"] {
    width: 100%;
  }

  /* the image block as light as the fields beside it (Cor do fundo, Opacidade) */
  .media-block > div {
    background: var(--color-neutral-base);

    /* on the lighter frame: a hint that reads, and the line above the actions showing */
    .hint:not(.error) {
      color: #a3a8ba;
    }

    > div:last-child {
      border-top-color: rgba(255, 255, 255, 0.12);

      > * + * {
        border-left-color: rgba(255, 255, 255, 0.12);
      }
    }
  }
`;

type Props = {
  background: Background;
  onChange: (background: Background) => void;
  siteId: string;
  /** The Welcome section's photo, which the blur uses. */
  profilePhoto?: string;
  /** The page's solid color, under a pattern's drawing (its samples show it). */
  pageColor: string;
  /** The page's accent (the buttons' color): ready-made gradients are drawn with it and the page's color. */
  accent: string;
  /** The page's solid color changed from a pattern (the same as the Cor background's). */
  onPageColor?: (color: string) => void;
  /** What Pexels opens searched for (the Keepo's field of work). */
  pexelsSearch?: string;
  /** An image or video closed without choosing one: the background goes back to what it was. */
  onCancel?: () => void;
};

/** The settings of a gradient, pattern, image, video or blurred background (the solid color is the theme's own field). */
export const BackgroundFields = ({
  background,
  onChange,
  siteId,
  profilePhoto,
  pageColor,
  accent,
  onPageColor,
  pexelsSearch,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.settings.groups.backgroundFields";
  // Imagem / Vídeo: the modal to pick one's own file or one from Pexels, right away while there's none
  const media = background.type === "image" || background.type === "video" ? background : undefined;
  const [picking, setPicking] = useState(!!media && !media.url);
  useEffect(() => {
    if (media && !media.url) setPicking(true);
  }, [media?.type, media?.url]); // eslint-disable-line react-hooks/exhaustive-deps
  // closed without choosing: nothing was added, so the background goes back to what it was
  const closePicker = () => {
    setPicking(false);
    if (media && !media.url) onCancel?.();
  };

  // A percentage slider, its value in its label (an image's or video's dim, a pattern's opacity)
  // A percentage in a box as tall and light as a field, its value on the right (a pattern's opacity, the blur)
  const percentBox = (label: string, value: number, min: number, max: number, change: (value: number) => void) => (
    <div className="opacity block">
      <span className="label" aria-hidden="true">
        {label}
      </span>
      <div className="box">
        <input
          type="range"
          aria-label={label}
          min={min}
          max={max}
          step={5}
          value={value}
          style={fill(value, min, max)}
          onChange={event => change(Number(event.target.value))}
        />
        <span className="value">{value}%</span>
      </div>
    </div>
  );

  // The image or video added, in the editor's image block: its thumbnail and "Trocar", both opening the modal
  // (one's own or one from Pexels)
  const mediaRow = (thumb: React.ReactNode, kind: "image" | "video") => (
    <div className="media-block">
      <ImageFieldRow
        shape="square"
        thumb={thumb}
        title={t(`pages.editor.settings.groups.backgroundMedia.title.${kind}`)}
        hint={
          kind === "video"
            ? t(`${prefix}.video.limits`)
            : t("pages.editor.settings.groups.backgroundMedia.ownHint.image")
        }
        wrapThumb={box => (
          <span role="presentation" onClick={() => setPicking(true)}>
            {box}
          </span>
        )}
        actions={
          <ImageFieldAction type="button" onClick={() => setPicking(true)}>
            {t(`${prefix}.${kind}.change`)}
          </ImageFieldAction>
        }
      />
    </div>
  );

  // An image's or video's own settings: the page's color under it, then how much of it shows and how dark it is
  const mediaFields = (media: Extract<Background, { type: "image" | "video" }>) => (
    <>
      <Divider />
      <FieldPair>
        <ColorField label={t(`${prefix}.media.background`)} color={pageColor} onPick={color => onPageColor?.(color)} />
      </FieldPair>
      <Divider />
      <FieldPair>
        {percentBox(t(`${prefix}.media.opacity`), media.opacity ?? 100, MIN_MEDIA_OPACITY, 100, value =>
          onChange({ ...media, opacity: value })
        )}
        {percentBox(t(`${prefix}.media.dim`), media.dim ?? 0, 0, MAX_DIM, value => onChange({ ...media, dim: value }))}
      </FieldPair>
    </>
  );

  if (background.type === "video") {
    // nothing added yet: only the modal, no empty box taking room under the kinds
    const Box = background.url ? Fields : Fragment;
    return (
      <Box>
        {background.url && (
          <>
            {mediaRow(
              background.poster ? <img src={background.poster} alt={t(`${prefix}.video.poster`)} /> : undefined,
              "video"
            )}
            {mediaFields(background)}
          </>
        )}
        {picking && (
          <BackgroundMediaModal
            kind="video"
            siteId={siteId}
            onExit={closePicker}
            search={pexelsSearch}
            onDone={({ url, poster }) => {
              setPicking(false);
              onChange({ ...background, url, poster });
            }}
          />
        )}
      </Box>
    );
  }

  if (background.type === "gradient") {
    const preset = background.preset;
    const style = preset ? "preset" : "custom";
    const pickStyle = (next: "custom" | "preset") =>
      next !== style &&
      onChange(
        next === "preset"
          ? { type: "gradient", preset: GRADIENT_PRESETS[0] }
          : { type: "gradient", from: pageColor, to: accent, angle: 180 }
      );
    return (
      <Fields>
        <div className="block">
          <span className="label">{t(`${prefix}.gradient.style`)}</span>
          <div className="options" role="radiogroup" aria-label={t(`${prefix}.gradient.style`)}>
            {(["custom", "preset"] as const).map(key => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={style === key}
                className={classNames({ selected: style === key })}
                onClick={() => pickStyle(key)}>
                {t(`${prefix}.gradient.styles.${key}`)}
              </button>
            ))}
          </div>
        </div>
        <Divider />
        {preset ? (
          <>
            <div className="block">
              <span className="label">{t(`${prefix}.gradient.presets`)}</span>
              <div className="presets" role="radiogroup" aria-label={t(`${prefix}.gradient.presets`)}>
                {GRADIENT_PRESETS.map(key => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={preset === key}
                    aria-label={t(`${prefix}.gradient.presetNames.${key}`)}
                    title={t(`${prefix}.gradient.presetNames.${key}`)}
                    className={classNames({ selected: preset === key })}
                    style={gradientPreset(key, pageColor, accent)}
                    onClick={() => onChange({ ...background, preset: key })}
                  />
                ))}
              </div>
            </div>
            <Divider />
            <div className="toggle">
              <span>
                <span className="label">{t(`${prefix}.gradient.animate`)}</span>
                <span className="hint">{t(`${prefix}.gradient.animateHint`)}</span>
              </span>
              <ToggleSwitch
                label={t(`${prefix}.gradient.animate`)}
                value={!!background.animate}
                setValue={animate => onChange({ ...background, animate })}
              />
            </div>
          </>
        ) : (
          <>
            <div className="block">
              <span className="label">{t(`${prefix}.gradient.direction`)}</span>
              <div className="directions" role="radiogroup" aria-label={t(`${prefix}.gradient.direction`)}>
                {DIRECTIONS.map(({ key, angle }) => {
                  const selected =
                    key === "radial" ? !!background.radial : !background.radial && background.angle === angle;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={t(`${prefix}.gradient.directions.${key}`)}
                      className={classNames({ selected })}
                      onClick={() =>
                        onChange(
                          key === "radial"
                            ? { ...background, radial: true }
                            : { ...background, angle, radial: undefined }
                        )
                      }>
                      {key === "radial" ? <RadialIcon /> : <Arrow angle={angle ?? 180} />}
                    </button>
                  );
                })}
              </div>
            </div>
            <Divider />
            <FieldPair>
              <ColorField
                label={t(`${prefix}.gradient.from`)}
                color={background.from ?? pageColor}
                onPick={from => onChange({ ...background, from })}
              />
              <ColorField
                label={t(`${prefix}.gradient.to`)}
                color={background.to ?? accent}
                onPick={to => onChange({ ...background, to })}
              />
            </FieldPair>
          </>
        )}
      </Fields>
    );
  }

  if (background.type === "pattern") {
    const position = background.position ?? "scroll";
    const size = background.size ?? "medium";
    const opacity = background.opacity ?? DEFAULT_PATTERN_OPACITY;
    return (
      <Fields>
        <FieldPair>
          <PatternPicker
            value={background.pattern}
            color={background.color}
            pageColor={pageColor}
            opacity={opacity}
            onPick={pattern => onChange({ ...background, pattern })}
          />
          {percentBox(t(`${prefix}.pattern.opacity`), opacity, MIN_PATTERN_OPACITY, MAX_DIM, value =>
            onChange({ ...background, opacity: value })
          )}
        </FieldPair>
        <Divider />
        {/* the page's color under the drawing, and the drawing's own */}
        <FieldPair>
          <ColorField
            label={t(`${prefix}.pattern.background`)}
            color={pageColor}
            onPick={color => onPageColor?.(color)}
          />
          <ColorField
            label={t(`${prefix}.pattern.color`)}
            color={background.color}
            onPick={color => onChange({ ...background, color })}
          />
        </FieldPair>
        <Divider />
        <FieldPair>
          <div className="block">
            <span className="label">{t(`${prefix}.pattern.size`)}</span>
            <div className="options" role="radiogroup" aria-label={t(`${prefix}.pattern.size`)}>
              {PATTERN_SIZES.map(key => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={size === key}
                  className={classNames({ selected: size === key })}
                  onClick={() => onChange({ ...background, size: key })}>
                  {t(`${prefix}.pattern.sizes.${key}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="block">
            <span className="label">{t(`${prefix}.pattern.position`)}</span>
            <div className="options" role="radiogroup" aria-label={t(`${prefix}.pattern.position`)}>
              {PATTERN_POSITIONS.map(key => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={position === key}
                  className={classNames({ selected: position === key })}
                  onClick={() => onChange({ ...background, position: key })}>
                  {key === "scroll" ? <ScrollIcon /> : <PinIcon />}
                  {t(`${prefix}.pattern.positions.${key}`)}
                </button>
              ))}
            </div>
          </div>
        </FieldPair>
      </Fields>
    );
  }

  if (background.type === "image") {
    // nothing added yet: only the modal, no empty box taking room under the kinds
    const Box = background.url ? Fields : Fragment;
    return (
      <Box>
        {background.url && (
          <>
            {mediaRow(<img src={background.url} alt="" />, "image")}
            {mediaFields(background)}
          </>
        )}
        {picking && (
          <BackgroundMediaModal
            kind="image"
            siteId={siteId}
            onExit={closePicker}
            search={pexelsSearch}
            onDone={({ url }) => {
              setPicking(false);
              onChange({ ...background, url });
            }}
          />
        )}
      </Box>
    );
  }

  const amount = background.amount ?? DEFAULT_BLUR;
  return (
    <Fields>
      <FieldPair>
        {percentBox(t(`${prefix}.blur.amount`), amount, MIN_BLUR, 100, value =>
          onChange({ ...background, amount: value })
        )}
      </FieldPair>
      <span className="hint">{t(`${prefix}.blur.${profilePhoto ? "hint" : "noPhoto"}`)}</span>
    </Fields>
  );
};
